/**
 * Checkout pricing — must match iOS CheckoutView.swift and Android CheckoutPricing.kt.
 * Source of truth for buyer apps: ios-buyer-app/buydropbuyer/Views/CheckoutView.swift
 */

/** iOS CheckoutView.stateTaxRates */
export const STATE_TAX_RATES: Record<string, number> = {
  AL: 0.04, AK: 0, AZ: 0.056, AR: 0.065, CA: 0.0725, CO: 0.029,
  CT: 0.0635, DE: 0, FL: 0.06, GA: 0.04, HI: 0.04, ID: 0.06,
  IL: 0.0625, IN: 0.07, IA: 0.06, KS: 0.065, KY: 0.06, LA: 0.0445,
  ME: 0.055, MD: 0.06, MA: 0.0625, MI: 0.06, MN: 0.06875, MS: 0.07,
  MO: 0.04225, MT: 0, NE: 0.055, NV: 0.0685, NH: 0, NJ: 0.06625,
  NM: 0.05125, NY: 0.04, NC: 0.0475, ND: 0.05, OH: 0.0575, OK: 0.045,
  OR: 0, PA: 0.06, RI: 0.07, SC: 0.06, SD: 0.045, TN: 0.07,
  TX: 0.0625, UT: 0.061, VT: 0.06, VA: 0.053, WA: 0.065, WV: 0.06,
  WI: 0.05, WY: 0.04, DC: 0.06,
};

/** iOS CheckoutView.zipToState — prefix map + default CA */
export function zipToStateFallback(zip: string): string {
  const digits = zip.replace(/\D/g, '');
  if (digits.length < 3) return 'CA';
  const prefix = digits.slice(0, 3);
  const map: Record<string, string> = {
    '900': 'CA', '100': 'NY', '750': 'TX', '330': 'FL', '606': 'IL',
    '191': 'PA', '432': 'OH', '303': 'GA', '282': 'NC', '482': 'MI',
  };
  return map[prefix] ?? 'CA';
}

/**
 * iOS: stateField.isEmpty ? (resolvedStateFromZip ?? zipToState(zipCode)) : stateField
 * Do not normalize full state names — iOS uses uppercase lookup with ?? 0.06 fallback.
 */
export function resolveTaxStateIos(
  stateField: string,
  resolvedStateFromZip: string | null | undefined,
  zipCode: string
): string {
  const field = (stateField || '').trim();
  if (field) return field.toUpperCase();
  const resolved = (resolvedStateFromZip || '').trim();
  if (resolved) return resolved.toUpperCase();
  return zipToStateFallback(zipCode);
}

export function getSalesTaxRate(stateForTax: string): number {
  return STATE_TAX_RATES[stateForTax] ?? 0.06;
}

/** iOS: (subtotal + deliveryFee) * rate, displayed with %.2f */
export function calculateSalesTaxAmount(
  subtotal: number,
  deliveryFee: number,
  stateForTax: string,
  hasDeliveryAddress: boolean
): number {
  if (!hasDeliveryAddress) return 0;
  const taxableAmount = Number(subtotal) + Number(deliveryFee);
  if (taxableAmount <= 0) return 0;
  const rate = getSalesTaxRate(stateForTax);
  return Number((taxableAmount * rate).toFixed(2));
}

export function calculateCheckoutTotal(
  subtotal: number,
  deliveryFee: number,
  salesTax: number
): number {
  return Number((subtotal + deliveryFee + salesTax).toFixed(2));
}

/** iOS deliveryFee — tier match, else last tier, else listing.shipping_fee ?? 2.99 */
export function calculateDeliveryFeeIos(
  listing: { delivery_fee_tiers?: string | null; shipping_fee?: number | null },
  deliveryDistance: number | null
): number {
  if (listing.delivery_fee_tiers && deliveryDistance !== null) {
    try {
      const tiers = JSON.parse(listing.delivery_fee_tiers) as Array<{ from: number; to: number; fee: number }>;
      if (Array.isArray(tiers) && tiers.length > 0) {
        const sorted = [...tiers].sort((a, b) => a.from - b.from);
        for (const tier of sorted) {
          if (deliveryDistance >= tier.from && deliveryDistance < tier.to) {
            return Number(tier.fee);
          }
        }
        const last = sorted[sorted.length - 1];
        if (last && deliveryDistance >= last.from) {
          return Number(last.fee);
        }
      }
    } catch {
      /* fall through */
    }
  }
  const shipping = listing.shipping_fee;
  if (shipping !== undefined && shipping !== null) {
    return Number(shipping);
  }
  return 2.99;
}

/** @deprecated Use calculateCheckoutSubtotal from listingPricing.ts */
export { calculateCheckoutSubtotal as calculateSubtotalIos } from './listingPricing';
